import { createOpenAIClient } from '~/utils/openaiClient';
import { reportError } from '~/utils/reportError';

const ROOM_NAME_MATCH_MODEL = 'gpt-4.1-mini';

type RoomNameMatch = { salesforceName: string; matchedRoomTypeName: string | null };

const ROOM_NAME_MATCH_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    matches: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          salesforceName: { type: 'string' },
          matchedRoomTypeName: { type: ['string', 'null'] },
        },
        required: ['salesforceName', 'matchedRoomTypeName'],
        additionalProperties: false,
      },
    },
  },
  required: ['matches'],
  additionalProperties: false,
};

function parseRoomNameMatchResponse(content: string): Array<RoomNameMatch> {
  try {
    const parsed = JSON.parse(content);
    const matches = parsed?.matches;

    if (!Array.isArray(matches)) {
      return [];
    }

    return matches.filter(
      (entry): entry is RoomNameMatch =>
        !!entry &&
        typeof entry.salesforceName === 'string' &&
        (entry.matchedRoomTypeName === null || typeof entry.matchedRoomTypeName === 'string'),
    );
  } catch {
    return [];
  }
}

// Fallback for reordered/reworded SF names `roomNamesMatch` can't reconcile; one batched call,
// only trusts matches against the real candidate list.
export async function resolveRoomNameMatchesWithAi(
  unmatchedSalesforceNames: Array<string | null | undefined>,
  candidateRoomTypeNames: Array<string | null | undefined>,
): Promise<Record<string, string>> {
  const uniqueUnmatchedNames = Array.from(
    new Set(unmatchedSalesforceNames.filter((name): name is string => !!name?.trim())),
  );
  const uniqueCandidateNames = Array.from(
    new Set(candidateRoomTypeNames.filter((name): name is string => !!name?.trim())),
  );

  if (uniqueUnmatchedNames.length === 0 || uniqueCandidateNames.length === 0) {
    return {};
  }

  try {
    const client = createOpenAIClient();
    const response = await client.responses.create({
      model: ROOM_NAME_MATCH_MODEL,
      input: [
        {
          role: 'system',
          content:
            'Match hotel room names between two lists that describe the same rooms with different wording, word order, or extra descriptors. Only match when confident they refer to the same room type.',
        },
        {
          role: 'user',
          content: [
            'List A room names need to be matched to list B room names.',
            'Names may differ in word order, phrasing, or punctuation (e.g. "1 King, Deluxe Pool View, Non Smoking" and "Deluxe King Pool View" describe the same room).',
            'Return one entry per list A name. Use "matchedRoomTypeName": null when no list B entry confidently matches. Otherwise use the exact string from list B.',
            `List A (Salesforce room names): ${JSON.stringify(uniqueUnmatchedNames)}`,
            `List B (existing room type names): ${JSON.stringify(uniqueCandidateNames)}`,
          ].join('\n'),
        },
      ],
      text: {
        format: {
          type: 'json_schema',
          name: 'room_name_matches',
          schema: ROOM_NAME_MATCH_RESPONSE_SCHEMA,
          strict: true,
        },
      },
    });

    const matches = parseRoomNameMatchResponse(response.output_text || '');
    const candidateNameSet = new Set(uniqueCandidateNames);

    return matches.reduce<Record<string, string>>((overrides, { salesforceName, matchedRoomTypeName }) => {
      if (matchedRoomTypeName && candidateNameSet.has(matchedRoomTypeName)) {
        overrides[salesforceName] = matchedRoomTypeName;
      }

      return overrides;
    }, {});
  } catch (error) {
    // Falls back to the substring-only match (unresolved rooms surface as "create new room"),
    // so a broken proxy/key/model degrades gracefully but shouldn't go unnoticed.
    reportError(error);
    return {};
  }
}
